/**
 * PatientTimelinePage — Timeline chronologique du dossier patient
 * US-2-06 : Vue mixte observations + traçabilités, filtrée et paginée
 *
 * Route : /patients/:patientId/timeline
 * Accès : tous les soignants authentifiés
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patientService } from '@/shared/services/patientService';
import { timelineService } from '@/shared/services/timelineService';
import type { Patient } from '@/shared/types/patient';
import type { TimelineEntry } from '@/shared/types/observation';

// ── Config affichage par type ─────────────────────────────────────────────────

const TYPE_CONFIG: Record<string, { icon: string; label: string; color: string }> = {
  tracabilite_medicamenteuse: { icon: 'bi-capsule',           label: 'Médicament',       color: 'primary' },
  transmission_dar:           { icon: 'bi-clipboard2-pulse',  label: 'Transmission DAR', color: 'info' },
  observation_libre:          { icon: 'bi-journal-text',      label: 'Observation',      color: 'secondary' },
  consultation_dossier:       { icon: 'bi-folder2-open',      label: 'Consultation',     color: 'warning' },
  commande_systeme:           { icon: 'bi-terminal',          label: 'Commande',         color: 'dark' },
  administration_medicament:  { icon: 'bi-capsule-pill',      label: 'Acte médic.',      color: 'primary' },
};

function getTypeConfig(type: string) {
  return TYPE_CONFIG[type] ?? { icon: 'bi-circle', label: type, color: 'secondary' };
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Composant entrée timeline ─────────────────────────────────────────────────

function TimelineCard({ entry, onValider }: { entry: TimelineEntry; onValider?: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = getTypeConfig(entry.type);

  return (
    <div className="d-flex gap-3 mb-3 fade-in">
      {/* Marqueur vertical */}
      <div className="d-flex flex-column align-items-center">
        <div
          className={`rounded-circle d-flex align-items-center justify-content-center bg-${cfg.color} text-white flex-shrink-0`}
          style={{ width: 36, height: 36, fontSize: 14 }}
        >
          <i className={`bi ${cfg.icon}`} />
        </div>
        <div className="flex-grow-1 border-start border-2 border-light mt-1" style={{ minHeight: 24 }} />
      </div>

      {/* Contenu */}
      <div className="card flex-grow-1 mb-0" style={{ cursor: 'pointer' }} onClick={() => setExpanded((e) => !e)}>
        <div className="card-body py-2 px-3">
          <div className="d-flex align-items-start justify-content-between gap-2 flex-wrap">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className={`badge bg-${cfg.color}-subtle text-${cfg.color}`}>
                <i className={`bi ${cfg.icon} me-1`} />
                {cfg.label}
              </span>
              {entry.kind === 'tracabilite' && (
                <span className="badge bg-warning-subtle text-warning">Traçabilité</span>
              )}
              <span className={`badge ${entry.statut === 'valide' ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                {entry.statut}
              </span>
            </div>
            <small className="text-muted text-nowrap">
              <i className="bi bi-clock me-1" />
              {formatDate(entry.created_at)}
            </small>
          </div>

          {/* Résumé du contenu (50 chars) */}
          <p className="mb-0 mt-1 text-truncate text-muted" style={{ fontSize: 13 }}>
            {entry.contenu_brut.slice(0, 120) || '(aucun contenu)'}
          </p>

          {/* Expansion — contenu complet + JSON structuré */}
          {expanded && (
            <div className="mt-2 border-top pt-2" onClick={(e) => e.stopPropagation()}>
              <p className="mb-1" style={{ fontSize: 13 }}>{entry.contenu_brut}</p>
              {entry.contenu_structure && (
                <pre className="bg-light rounded p-2 mb-2" style={{ fontSize: 11 }}>
                  {JSON.stringify(entry.contenu_structure, null, 2)}
                </pre>
              )}
              {entry.kind === 'observation' && entry.statut === 'brouillon' && onValider && (
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => onValider(entry.id)}
                >
                  <i className="bi bi-check2-circle me-1" />
                  Valider
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────

type Filter = 'all' | 'observation' | 'tracabilite';

export function PatientTimelinePage() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!patientId) return;
    setIsLoading(true);
    setError('');
    try {
      const [pat, timeline] = await Promise.all([
        patientService.getById(patientId),
        timelineService.getTimeline(patientId),
      ]);
      setPatient(pat);
      setEntries(timeline);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const handleValider = async (observationId: string) => {
    try {
      await timelineService.validerObservation(observationId);
      await load(); // recharger
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur validation');
    }
  };

  const filtered = filter === 'all' ? entries : entries.filter((e) => e.kind === filter);

  const obsCount = entries.filter((e) => e.kind === 'observation').length;
  const tracCount = entries.filter((e) => e.kind === 'tracabilite').length;

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* En-tête patient */}
      <div className="row mb-4 align-items-center">
        <div className="col">
          <button className="btn btn-link text-muted p-0 mb-2" onClick={() => navigate('/patients')}>
            <i className="bi bi-arrow-left me-1" />
            Patients
          </button>
          <h1 className="text-gradient fw-bold mb-0">
            <i className="bi bi-clock-history me-2" />
            Dossier patient
          </h1>
          {patient && (
            <p className="text-muted mb-0 mt-1">
              <strong>{patient.prenom} {patient.nom}</strong>
              <span className="mx-2 text-muted">·</span>
              <code className="text-primary">{patient.ipp}</code>
              {patient.service_actuel && (
                <>
                  <span className="mx-2 text-muted">·</span>
                  <span className="badge bg-primary-subtle text-primary">{patient.service_actuel}</span>
                </>
              )}
            </p>
          )}
        </div>
        <div className="col-auto">
          <button className="btn btn-outline-primary" onClick={load}>
            <i className="bi bi-arrow-clockwise me-1" />
            Rafraîchir
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center fade-in mb-3">
          <i className="bi bi-exclamation-triangle-fill me-2" />
          {error}
        </div>
      )}

      {/* Filtres + compteurs */}
      <div className="d-flex align-items-center gap-2 mb-4 flex-wrap">
        <button
          className={`btn btn-sm ${filter === 'all' ? 'btn-primary shadow-glow' : 'btn-outline-primary'}`}
          onClick={() => setFilter('all')}
        >
          Tout ({entries.length})
        </button>
        <button
          className={`btn btn-sm ${filter === 'observation' ? 'btn-info shadow-glow' : 'btn-outline-info'}`}
          onClick={() => setFilter('observation')}
        >
          <i className="bi bi-clipboard2-pulse me-1" />
          Observations ({obsCount})
        </button>
        <button
          className={`btn btn-sm ${filter === 'tracabilite' ? 'btn-warning shadow-glow' : 'btn-outline-warning'}`}
          onClick={() => setFilter('tracabilite')}
        >
          <i className="bi bi-capsule me-1" />
          Traçabilités ({tracCount})
        </button>
      </div>

      {/* Timeline */}
      {filtered.length === 0 ? (
        <div className="card fade-in">
          <div className="card-body text-center py-5 text-muted">
            <i className="bi bi-inbox fs-1 d-block mb-2" />
            <p className="mb-0">Aucune entrée pour ce patient.</p>
            <small>Utiliser la <strong>Saisie vocale</strong> pour créer des observations.</small>
          </div>
        </div>
      ) : (
        <div>
          {filtered.map((entry) => (
            <TimelineCard
              key={`${entry.kind}-${entry.id}`}
              entry={entry}
              onValider={handleValider}
            />
          ))}
        </div>
      )}
    </div>
  );
}
